import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useClinic } from '@/contexts/ClinicContext';
import { cn } from '@/lib/utils';
import {
  Menu,
  Bell,
  LogOut,
  User,
  Settings,
  ChevronRight,
  Home,
  Building2,
  Check,
  ChevronDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import Sidebar from './Sidebar';
import { toast } from 'sonner';
import { getRoleBadgeColor } from '@/constants';

// Breadcrumb mapping
const breadcrumbLabels = {
  dashboard: 'Dashboard',
  patients: 'Patients',
  appointments: 'Appointments',
  consultations: 'Consultations',
  doctors: 'Doctors',
  reports: 'Reports',
  settings: 'Settings',
  'my-appointments': 'Appointments',
  'my-schedule': 'Dashboard',
  new: 'New',
  edit: 'Edit',
};

const Topbar = ({ onMenuClick, sidebarCollapsed }) => {
  const { user, logout, hasPermission, userRoles = [] } = useAuth();
  const { hospitalId, hospitalName, activeClinic, availableClinics, switchClinic, buildUrl } = useClinic();
  const navigate = useNavigate();
  const location = useLocation();
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(true);

  const canSwitchClinics = (user?.assignedClinics && user.assignedClinics.length > 1) || hasPermission('CLINIC_ALL_VIEW') || hasPermission('PLATFORM_MANAGE');

  // Filter available clinics to those assigned to user, unless user has CLINIC_ALL_VIEW / PLATFORM_MANAGE
  const userClinics = hasPermission('CLINIC_ALL_VIEW') || hasPermission('PLATFORM_MANAGE')
    ? availableClinics
    : availableClinics.filter((c) => !user?.assignedClinics || user.assignedClinics.includes(c.id));

  // Generate clean breadcrumbs without IDs
  const generateBreadcrumbs = () => {
    const segments = location.pathname.split('/').filter(Boolean);
    const clinicIndex = segments.indexOf('clinic');
    const relevantSegments = clinicIndex !== -1 && clinicIndex + 2 <= segments.length
      ? segments.slice(clinicIndex + 2)
      : segments.filter(s => s !== 'hospital' && s !== 'clinic' && s !== 'app');

    const breadcrumbs = [];
    let subpathAccum = '';

    relevantSegments.forEach((segment, index) => {
      subpathAccum += (subpathAccum ? `/${segment}` : segment);

      // Never show raw IDs in breadcrumbs
      const isId = segment.startsWith('pat-') || segment.startsWith('apt-') || segment.startsWith('cons-') || segment.startsWith('doc-') || segment.startsWith('usr-') || (!isNaN(segment) && segment.length > 3);
      if (isId) {
        return; // Skip IDs from breadcrumb list
      }

      const label = breadcrumbLabels[segment] || (segment.charAt(0).toUpperCase() + segment.slice(1));

      breadcrumbs.push({
        label,
        subpath: subpathAccum,
        isLast: index === relevantSegments.length - 1,
      });
    });

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleClinicChange = (clinic) => {
    const match = location.pathname.match(/\/hospital\/[^/]+\/clinic\/[^/]+\/(.*)/);
    const subpath = match ? match[1] : 'dashboard';
    switchClinic(clinic.id, navigate, subpath);
    toast.success(`Switched active clinic to ${clinic.name}`);
  };

  const userDesignation = user?.showDesignationInUi !== false
    ? (user?.commonRole || user?.designation || null)
    : null;

  return (
    <header
      data-testid="topbar"
      className={cn(
        'fixed top-0 right-0 z-40 h-16 bg-white/95 backdrop-blur border-b border-slate-200/80 flex items-center justify-between px-4 md:px-6 transition-all duration-300 print:hidden',
        sidebarCollapsed ? 'left-0 md:left-[72px]' : 'left-0 md:left-64'
      )}
    >
      {/* Left Side - Mobile Menu & Context / Breadcrumbs */}
      <div className="flex items-center gap-3 md:gap-4">
        {/* Mobile Menu */}
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="md:hidden" data-testid="mobile-menu-trigger">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-64">
            <Sidebar collapsed={false} onToggle={() => {}} />
          </SheetContent>
        </Sheet>

        {/* Static Clinic Indicator (Pure static text - Switcher is now inside profile dropdown) */}
        <div
          className="h-9 px-3 border border-slate-200 bg-slate-50 text-slate-700 font-medium flex items-center gap-2 rounded-md select-none"
          data-testid="clinic-static-indicator"
        >
          <Building2 className="h-4 w-4 text-teal-600 shrink-0" />
          <span className="hidden sm:inline text-xs text-slate-500 font-normal">
            {hospitalName || 'Hospital'} •
          </span>
          <span className="font-semibold text-xs sm:text-sm text-slate-800 truncate max-w-[150px] md:max-w-[220px]">
            {activeClinic?.name || 'Active Clinic'}
          </span>
        </div>

        {/* Desktop Breadcrumbs (Major module titles only, no IDs) */}
        {breadcrumbs.length > 0 && (
          <nav className="hidden lg:flex items-center gap-1.5 text-xs text-slate-500 pl-2 border-l border-slate-200">
            <button
              onClick={() => navigate(buildUrl('dashboard'))}
              className="flex items-center gap-1 hover:text-slate-900 transition-colors"
            >
              <Home className="h-3.5 w-3.5" />
            </button>
            {breadcrumbs.map((crumb, i) => (
              <React.Fragment key={crumb.subpath || i}>
                <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
                {crumb.isLast ? (
                  <span className="font-medium text-slate-900">{crumb.label}</span>
                ) : (
                  <button
                    onClick={() => navigate(buildUrl(crumb.subpath))}
                    className="hover:text-slate-900 transition-colors"
                  >
                    {crumb.label}
                  </button>
                )}
              </React.Fragment>
            ))}
          </nav>
        )}
      </div>

      {/* Right Side - Designation Badge, Notifications & Profile */}
      <div className="flex items-center gap-2 sm:gap-3">
        {/* Designation Badge: Shown only if showDesignationInUi is not false, rendered cleanly without colors */}
        {userDesignation && (
          <Badge
            variant="outline"
            className="font-medium text-xs px-2.5 py-0.5 bg-slate-100 text-slate-700 border-slate-200"
          >
            {userDesignation}
          </Badge>
        )}

        {/* Notifications Popover */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="relative text-slate-600 hover:text-slate-900"
              data-testid="notifications-button"
            >
              <Bell className="h-5 w-5" />
              {hasUnreadNotifications && (
                <span className="absolute top-2 right-2 w-2 h-2 bg-teal-600 rounded-full ring-2 ring-white" />
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80">
            <DropdownMenuLabel className="flex items-center justify-between">
              <span>Clinic Notifications</span>
              <Badge variant="secondary" className="text-xs">2 New</Badge>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <div className="space-y-1 p-1">
              <div className="p-2.5 rounded-lg bg-teal-50/70 hover:bg-teal-50 transition-colors cursor-pointer text-xs">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-bold text-teal-900">Dr. Priya Sharma</span>
                  <span className="text-[10px] text-slate-400">10m ago</span>
                </div>
                <p className="text-slate-600">Patient Rahul Verma checked in for consultation.</p>
              </div>
              <div className="p-2.5 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer text-xs">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-bold text-slate-800">OPD Queue Alert</span>
                  <span className="text-[10px] text-slate-400">25m ago</span>
                </div>
                <p className="text-slate-500">Wait time in Cardiology OPD reached 25 mins.</p>
              </div>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-center justify-center text-xs text-teal-600 font-medium cursor-pointer"
              onClick={() => setHasUnreadNotifications(false)}
            >
              Mark all as read
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* User Profile Menu with in-place Clinic Switcher */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="flex items-center gap-2 pl-2 pr-3 py-1.5 h-auto hover:bg-slate-100 rounded-full"
              data-testid="user-menu-button"
            >
              <Avatar className="h-8 w-8 ring-2 ring-teal-500/20">
                <AvatarImage src={user?.avatar} />
                <AvatarFallback className="bg-teal-600 text-white text-xs font-semibold">
                  {user?.firstName?.[0]}{user?.lastName?.[0]}
                </AvatarFallback>
              </Avatar>
              <div className="hidden md:flex flex-col items-start text-left">
                <span className="text-xs font-bold text-slate-900 leading-none">
                  {user?.firstName} {user?.lastName}
                </span>
                {userDesignation ? (
                  <span className="text-[10px] text-slate-500 capitalize leading-none mt-0.5">
                    {userDesignation}
                  </span>
                ) : null}
              </div>
              <ChevronDown className="h-3.5 w-3.5 text-slate-400 hidden md:block" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel>
              <div className="flex flex-col">
                <span className="text-sm font-medium text-slate-900">{user?.firstName} {user?.lastName}</span>
                <span className="text-xs text-slate-500 truncate">{user?.email}</span>
                {userDesignation && (
                  <span className="text-[11px] text-slate-400 mt-0.5">{userDesignation}</span>
                )}
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />

            {/* In-Place Clinic Branch Switcher (Shown if user has multiple assigned clinics or CLINIC_ALL_VIEW) */}
            {canSwitchClinics && userClinics.length > 1 && (
              <>
                <DropdownMenuLabel className="text-xs text-slate-500 font-semibold uppercase tracking-wider">
                  Switch Clinic Branch
                </DropdownMenuLabel>
                {userClinics.map((clinic) => {
                  const isSelected = clinic.id === activeClinic?.id;
                  return (
                    <DropdownMenuItem
                      key={clinic.id}
                      onClick={() => handleClinicChange(clinic)}
                      className="flex items-center justify-between cursor-pointer py-2"
                      data-testid={`profile-clinic-option-${clinic.id}`}
                    >
                      <div className="flex flex-col truncate pr-2">
                        <span className={cn('text-xs font-medium truncate', isSelected ? 'text-teal-700 font-semibold' : 'text-slate-700')}>
                          {clinic.name}
                        </span>
                        <span className="text-[10px] text-slate-400 truncate">{clinic.floor || clinic.code}</span>
                      </div>
                      {isSelected && <Check className="h-3.5 w-3.5 text-teal-600 shrink-0" />}
                    </DropdownMenuItem>
                  );
                })}
                <DropdownMenuSeparator />
              </>
            )}

            <DropdownMenuItem onClick={() => navigate(buildUrl('settings'))} className="cursor-pointer">
              <User className="mr-2 h-4 w-4 text-slate-500" />
              <span>Profile Settings</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate(buildUrl('settings'))} className="cursor-pointer">
              <Settings className="mr-2 h-4 w-4 text-slate-500" />
              <span>Clinic Preferences</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-600 cursor-pointer">
              <LogOut className="mr-2 h-4 w-4" />
              <span>Sign out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

export default Topbar;
